import { FunctionComponent, useCallback, useContext, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useParams } from 'react-router-dom';
import { UserContext } from '../../../context';
import { OrganisationTagType, storyCardApi } from '../../../services';
import {
  cookieOptions,
  Filter,
  FilterGroup,
  FilterValue,
  optArrItem,
  TAGS_FEED_ADMIN_COOKIE_KEY,
  useAppDispatch,
  useAuthorsFilter,
  useCardStatusFilter,
  useCardTypeFilter,
  useDateRangeFilter,
  useFilter,
  useTags,
  useTagsFilter,
} from '../../../shared';
import { getTagsFeed } from '../../../slices';

import classes from './TagsFeedAdminRightSide.module.scss';

export const TagsFeedAdminRightSide: FunctionComponent = () => {
  const dispatch = useAppDispatch();

  const organisationTagId = Number(useParams().organisationTagId);

  const { hasInternalTags, hasExternalTags, hasExternalStatusTags } = useTags();

  const { getGroupValue, getDateRangeValue } = useFilter();

  const [fetchAuthors] = storyCardApi.endpoints.storyCardAuthorsFiltersByChannelId.useLazyQuery();

  const [cookies, setCookie] = useCookies([TAGS_FEED_ADMIN_COOKIE_KEY]);

  const [filter, setFilter] = useState<FilterValue[]>(
    cookies[TAGS_FEED_ADMIN_COOKIE_KEY]?.filter ?? []
  );

  const { channelId } = useContext(UserContext).userInfo.userData;

  const { group: dateRangeGroup } = useDateRangeFilter({ filter });

  const { group: authorsGroup } = useAuthorsFilter({
    filter,
    fetchItemsQuery: fetchAuthors,
    fetchItemsQueryArgs: { channelId },
  });

  const { group: internalTagsGroup } = useTagsFilter({
    filter,
    types: [OrganisationTagType.INTERNAL],
  });

  const { group: externalTagsGroup } = useTagsFilter({
    filter,
    types: [OrganisationTagType.EXTERNAL],
  });

  const { group: externalStatusTagsGroup } = useTagsFilter({
    filter,
    types: [OrganisationTagType.EXTERNAL_STATUS],
  });

  const { group: cardTypeGroup } = useCardTypeFilter();

  const { group: cardStatusGroup } = useCardStatusFilter();

  const groups: FilterGroup[] = useMemo(
    () => [
      ...optArrItem(hasInternalTags, internalTagsGroup),
      ...optArrItem(hasExternalTags, externalTagsGroup),
      ...optArrItem(hasExternalStatusTags, externalStatusTagsGroup),
      authorsGroup,
      dateRangeGroup,
      cardTypeGroup,
      cardStatusGroup,
    ],
    [
      authorsGroup,
      cardStatusGroup,
      cardTypeGroup,
      dateRangeGroup,
      externalStatusTagsGroup,
      externalTagsGroup,
      hasExternalStatusTags,
      hasExternalTags,
      hasInternalTags,
      internalTagsGroup,
    ]
  );

  const filterChange = useCallback(
    (filter: FilterValue[]) => {
      const commonIdProps = { filter, multiCheck: true, propToMap: 'id' };

      const internalTagsId = getGroupValue({ ...commonIdProps, groupId: internalTagsGroup.id });

      const externalTagsId = getGroupValue({ ...commonIdProps, groupId: externalTagsGroup.id });

      const externalStatusTagsId = getGroupValue({
        ...commonIdProps,
        groupId: externalStatusTagsGroup.id,
      });

      const authorId = getGroupValue({ ...commonIdProps, groupId: authorsGroup.id });

      const storyCardType = getGroupValue({ filter, groupId: cardTypeGroup.id, multiCheck: true });

      const status = getGroupValue({ filter, groupId: cardStatusGroup.id, multiCheck: true });

      const { dateRange, from, to } = getDateRangeValue({ filter, groupId: dateRangeGroup.id });

      const common = {
        ...((internalTagsId?.length || externalTagsId?.length || externalStatusTagsId?.length) && {
          organisationTags: { internalTagsId, externalTagsId, externalStatusTagsId },
        }),
        ...(authorId?.length && { authorId }),
        ...(storyCardType?.length && { storyCardType }),
        ...(status?.length && { status }),
      };

      const requestFilterToSave = { ...common, dateRange };

      const filters = { ...common, ...(from && { from }), ...(to && { to }) };

      setCookie(
        TAGS_FEED_ADMIN_COOKIE_KEY,
        { requestFilter: requestFilterToSave, filter },
        cookieOptions()
      );

      setFilter(filter);

      dispatch(getTagsFeed({ channelId, organisationTagId, filters }));
    },
    [
      authorsGroup.id,
      cardStatusGroup.id,
      cardTypeGroup.id,
      channelId,
      dateRangeGroup.id,
      dispatch,
      externalStatusTagsGroup.id,
      externalTagsGroup.id,
      getDateRangeValue,
      getGroupValue,
      internalTagsGroup.id,
      organisationTagId,
      setCookie,
    ]
  );

  return (
    <div className={classes['tags-feed-admin-right-side']}>
      <Filter filter={filter} groups={groups} onChange={filterChange} resetPageScrollOnChange />
    </div>
  );
};
